import React from 'react';
import { useNavigate } from 'react-router-dom';
import { getAdUrl } from '../../utils/urlHelpers';
import { formatDate, formatPrice } from '../../utils/helpers';
import { getAdCoverImageUrl, IMAGE_PLACEHOLDER_PATH } from '../../utils/imageFallback';

const AdCardMinimal = ({ ad }) => {
  const navigate = useNavigate();

  const handleClick = () => {
    navigate(getAdUrl(ad));
  };

  const formattedDate = ad.created_at ? formatDate(ad.created_at) : 'Recently';
  const priceDisplay = formatPrice(ad.price);
  const coverUrl = getAdCoverImageUrl(ad);

  return (
    <div
      onClick={handleClick}
      className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer overflow-hidden flex-shrink-0 w-80 h-96 flex flex-col group transform hover:-translate-y-1"
    >
      {/* Image Container */}
      <div className="relative h-48 overflow-hidden bg-gradient-to-br from-green-100 to-blue-100">
        {coverUrl !== IMAGE_PLACEHOLDER_PATH ? (
          <img
            src={coverUrl}
            alt={ad.title}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
            loading="lazy"
            onError={(e) => {
              e.currentTarget.src = IMAGE_PLACEHOLDER_PATH;
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
        )}
        
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        
      </div>

      {/* Content */}
      <div className="p-4 flex flex-col flex-grow">
        {/* Title */}
        <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-green-600 transition-colors">
          {ad.title}
        </h3>

        {/* Description Preview */}
        {ad.description && (
          <p className="text-sm text-gray-600 mb-3 line-clamp-2 flex-grow">
            {ad.description}
          </p>
        )}

        {/* Ad Details */}
        <div className="space-y-2 mb-3">
          {/* Price */}
          <div className="flex items-center justify-between">
            <span className="text-lg font-bold text-green-600">
              {priceDisplay}
            </span>
            <span className="text-xs text-gray-500">{formattedDate}</span>
          </div>

          {/* Society */}
          {ad.society && (
            <div className="flex items-center text-sm text-gray-600">
              <svg className="w-4 h-4 mr-2 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              <span className="truncate">{typeof ad.society === 'object' ? ad.society.name : ad.society}</span>
            </div>
          )}

          {/* Views */}
          <div className="flex items-center text-sm text-gray-500">
            <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            <span>{ad.views || 0} views</span>
          </div>
        </div>

        {/* View Details Link */}
        <div className="mt-auto pt-2 border-t border-gray-100">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-green-600 group-hover:text-green-700">
              View Details
            </span>
            <svg className="w-4 h-4 text-green-600 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdCardMinimal;

